import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { OpenNav, PANE_FRAME, StatusChip, type PaneLayout } from '@/design';
import { formatDateTime } from '@/domains/survey';
import { ddApi } from '../../api/dd-api';
import { figuresApi } from '../../api/figures-api';
import type { IListRow } from '../../api/lists-contracts';
import type { IAlsoOwes, IFigureSeries, IThresholdCondition } from '../../api/metrics-updates-contracts';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { recordSurveyPath } from '../../components/participant-link';
import { ORIGIN_WORDS, wordFor } from '../../figures';
import { listSurveyLabel } from '../../lists';
import { compactMoney, pairKey, relationWords, seriesLabel } from '../../metrics-updates';
import { RowActions } from './row-actions';

/**
 * A Metrics updates row opened beside the list (SR-315; DD calls' panel the precedent): what an
 * analyst wants before writing or calling — the editions the advisor is on, their figures as of
 * the last dates the record holds (the record page's figures table, read here; the threshold's
 * series first), the requests recorded for figures, where else they owe figures — and the three
 * presses as plain buttons. ↑/↓ (Previous / Next) walk the page's rows; Esc closes it by the
 * split view's rule.
 */

/** How many as-of dates the panel shows: the round's and the ones before it. */
const DATES = 4;

export function MetricsPanel({
  layout,
  row,
  name,
  meta,
  editions,
  alsoOwes,
  survey,
  asOf,
  series,
  thresholdSeries,
  version,
  previous,
  next,
  onClose,
  onDone
}: {
  /** Where the panel is drawn (*Opens in*). */
  layout: PaneLayout;
  row: IListRow;
  name: string;
  /** Firm, place — a line under the name. */
  meta: string;
  editions: readonly string[];
  alsoOwes: readonly IAlsoOwes[];
  survey: string;
  asOf: string;
  series: readonly IFigureSeries[];
  thresholdSeries: readonly IThresholdCondition[];
  /** Moved by the page after a write, so the panel reads again. */
  version: number;
  previous: Nullable<() => void>;
  next: Nullable<() => void>;
  onClose: () => void;
  onDone: (sentence: string) => void;
}) {
  const titleId = React.useId();
  const figures = useAdminData(
    (signal) => figuresApi.table(row.participantId, survey, signal),
    [row.participantId, survey, version]
  );
  const outreach = useAdminData(
    async (signal) =>
      (await ddApi.outreach(row.participantId, survey, signal)).filter((attempt) => attempt.purpose === 'metrics'),
    [row.participantId, survey, version]
  );

  // The threshold's series first, then the rest of the catalogue the table holds.
  const table = figures.data;
  const order = table
    ? [...table.metrics.keys()].sort((a, b) => {
        const rank = (index: number) => {
          const metric = table.metrics[index];
          const at = thresholdSeries.findIndex((entry) => entry.metric === metric.metric && entry.scope === metric.scope);
          return at < 0 ? 100 + index : at;
        };
        return rank(a) - rank(b);
      })
    : [];
  const shown = order.slice(0, Math.max(thresholdSeries.length, 2));
  const dates = (table?.rows ?? []).slice(0, DATES);

  return (
    <section
      aria-labelledby={titleId}
      data-metrics-panel={row.participantId}
      className={cn('grid content-start gap-4 bg-card', PANE_FRAME[layout])}
    >
      <div className="flex items-start gap-3">
        <div className="grid min-w-0 flex-1 gap-0.5">
          <h2 id={titleId} className="truncate text-base font-medium text-[var(--color-royal-blue)]">
            {name}
          </h2>
          <span className="text-xs text-muted-foreground">{[meta, listSurveyLabel(survey)].filter(Boolean).join(' · ')}</span>
          <Link
            to={recordSurveyPath(row.participantId, survey)}
            className="text-sm font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open the record page
          </Link>
        </div>
        <OpenNav
          previous={previous}
          next={next}
          newTab={recordSurveyPath(row.participantId, survey)}
          onClose={onClose}
        />
      </div>

      {editions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[0.8125rem]">
          <span className="text-muted-foreground">On</span>
          {editions.map((label) => (
            <StatusChip key={label} tone="neutral">
              {label}
            </StatusChip>
          ))}
        </div>
      )}

      <div className="grid gap-2">
        <h3 className="text-sm font-semibold">Figures</h3>
        {figures.error && <LoadError message={figures.error} />}
        {table && dates.length === 0 && <p className="text-sm text-muted-foreground">No figures on the record yet.</p>}
        {table && dates.length > 0 && (
          <table className="text-[0.8125rem]" data-panel-figures>
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3 font-normal">As of</th>
                {shown.map((index) => (
                  <th key={index} className="py-1 pr-3 text-right font-normal">
                    {seriesLabel(series, table.metrics[index].metric, table.metrics[index].scope)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((entry) => (
                <tr key={entry.asOf} className={entry.asOf === asOf ? 'font-semibold' : undefined} data-as-of={entry.asOf}>
                  <td className="py-1 pr-3">{formatDay(entry.asOf)}</td>
                  {shown.map((index) => {
                    const cell = entry.cells[index];
                    return (
                      <td key={index} className="py-1 pr-3 text-right tabular-nums">
                        {cell?.value == null ? '—' : compactMoney(cell.value)}
                        {cell && (
                          <span className="block text-[0.7rem] font-normal text-muted-foreground">
                            {wordFor(ORIGIN_WORDS, cell.origin)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs text-muted-foreground">Asked as of {formatDay(asOf)}.</p>
      </div>

      <div className="grid gap-2">
        <h3 className="text-sm font-semibold">Requests for figures</h3>
        {outreach.error && <LoadError message={outreach.error} />}
        {outreach.data && outreach.data.length === 0 && <p className="text-sm text-muted-foreground">None recorded yet.</p>}
        {outreach.data && outreach.data.length > 0 && (
          <ul className="grid gap-1 text-[0.8125rem]" data-panel-requests>
            {outreach.data.map((attempt) => (
              <li key={attempt.outreachId}>
                {formatDateTime(attempt.occurredAt)} · {attempt.channel}
                {attempt.recordedBy?.email && <span className="text-muted-foreground"> · {attempt.recordedBy.email}</span>}
                {attempt.note && <span className="text-muted-foreground"> · {attempt.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {alsoOwes.length > 0 && (
        <div className="grid gap-1 text-[0.8125rem]" data-panel-also-owes>
          <h3 className="text-sm font-semibold">Also owes figures</h3>
          {alsoOwes.map((entry) => (
            <Link
              key={pairKey(entry)}
              className="underline-offset-2 hover:underline"
              to={recordSurveyPath(entry.participantId, entry.survey)}
            >
              {listSurveyLabel(entry.survey)} · {entry.displayName}{' '}
              <span className="text-muted-foreground">({relationWords(entry.relation)})</span>
            </Link>
          ))}
        </div>
      )}

      <RowActions
        row={row}
        name={name}
        survey={survey}
        asOf={asOf}
        series={series}
        thresholdSeries={thresholdSeries}
        layout="panel"
        onDone={onDone}
      />
    </section>
  );
}
